import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, TrendingUp, Users, ShoppingCart } from 'lucide-react';

const SavingsCalculator = () => {
  const navigate = useNavigate();
  const [familySize, setFamilySize] = useState(4);
  const [selectedCategories, setSelectedCategories] = useState([]);
  
  const categories = [
    { id: 'groceries', name: 'Groceries', avgSaving: 30, icon: '🛒' },
    { id: 'meat', name: 'Meat & Protein', avgSaving: 40, icon: '🥩' },
    { id: 'produce', name: 'Fresh Produce', avgSaving: 35, icon: '🥬' },
    { id: 'household', name: 'Household Items', avgSaving: 25, icon: '🏠' },
    { id: 'personal', name: 'Personal Care', avgSaving: 20, icon: '🧴' }
  ];
  
  const calculateAnnualSavings = () => {
    const monthlySpend = familySize * 150; // Average per person
    
    return selectedCategories.reduce((total, categoryId) => {
      const category = categories.find(c => c.id === categoryId);
      const categorySaving = (monthlySpend * (category.avgSaving / 100)) * 12;
      return total + categorySaving;
    }, 0);
  };
  
  const savings = calculateAnnualSavings();
  const jobsCreated = Math.floor(savings / 500); // Rough estimate
  const localImpact = savings * 0.95; // 95% stays local
  
  return (
    <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-lg p-6 shadow-md mb-6">
      <h2 className="text-2xl font-bold mb-4 text-gray-800">Calculate Your Annual Savings</h2>
      
      <div className="space-y-4">
        {/* Family Size Selector */}
        <div className="bg-white rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Family Size: <span className="text-blue-600 font-bold">{familySize} people</span>
          </label>
          <input
            type="range"
            min="1"
            max="8"
            value={familySize}
            onChange={(e) => setFamilySize(parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>1</span>
            <span>8</span>
          </div>
        </div>
        
        {/* Category Selector */}
        <div className="bg-white rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            What do you buy regularly?
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`p-3 rounded-lg border-2 transition-all ${
                  selectedCategories.includes(cat.id)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => {
                  setSelectedCategories(prev =>
                    prev.includes(cat.id)
                      ? prev.filter(id => id !== cat.id)
                      : [...prev, cat.id]
                  );
                }}
              >
                <div className="text-2xl mb-1">{cat.icon}</div>
                <div className="text-sm font-medium">{cat.name}</div>
                <div className="text-xs text-green-600 font-semibold">
                  Save {cat.avgSaving}%
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Savings Display */}
      {selectedCategories.length > 0 && (
        <div className="mt-6 bg-white rounded-lg p-6 shadow-inner">
          <div className="text-center mb-4">
            <div className="text-sm text-gray-600 mb-1">You Could Save</div>
            <div className="text-4xl font-bold text-green-600">
              ${savings.toLocaleString()}
            </div>
            <div className="text-gray-600">per year</div>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center">
              <div className="flex justify-center mb-1">
                <Users className="text-blue-600" size={24} />
              </div>
              <div className="text-2xl font-bold text-blue-600">{jobsCreated}</div>
              <div className="text-xs text-gray-600">Jobs Created</div>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-1">
                <TrendingUp className="text-purple-600" size={24} />
              </div>
              <div className="text-2xl font-bold text-purple-600">
                ${localImpact.toLocaleString()}
              </div>
              <div className="text-xs text-gray-600">Kept Local</div>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-1">
                <ShoppingCart className="text-orange-600" size={24} />
              </div>
              <div className="text-2xl font-bold text-orange-600">
                {selectedCategories.length}
              </div>
              <div className="text-xs text-gray-600">Categories</div>
            </div>
          </div>
          
          <button
            onClick={() => navigate('/groups')}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Start Saving Now →
          </button>
        </div>
      )}
    </div>
  );
};

export default SavingsCalculator;